import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import lang from "translations";

export const REGISTERED_GUESTS_COLOR = "#2F6F76";
export const TOTAL_GUESTS_COLOR = "#A9D2D6";

export const GuestView = {
  Both: "BOTH",
  Registered: "REGISTERED",
  Total: "TOTAL",
};

const MAX_SLOTS_WITH_VALUE_LABEL = 20;

// draws the total of the stack on top of each column
const stackTotalPlugin = {
  id: "arrivalTimeStackTotal",
  afterDatasetsDraw: (chart) => {
    const { ctx, data } = chart;
    if (data.labels.length > MAX_SLOTS_WITH_VALUE_LABEL) {
      return;
    }
    const topMeta = chart.getDatasetMeta(data.datasets.length - 1);
    topMeta.data.forEach((bar, index) => {
      const total = data.datasets.reduce(
        (sum, dataset) => sum + (dataset.data[index] || 0),
        0
      );
      if (!total) {
        return;
      }
      ctx.save();
      ctx.fillStyle = "#464C4E";
      ctx.font = "600 12px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(total, bar.x, bar.y - 6);
      ctx.restore();
    });
  },
};

const ArrivalTimeChart = ({ slots = [], height = 360, guestView = GuestView.Both }) => {
  const labels = useMemo(() => slots.map(({ label }) => label), [slots]);

  const datasets = useMemo(() => {
    const barOptions = {
      stack: "guests",
      barPercentage: 0.3,
      categoryPercentage: 0.75,
      maxBarThickness: 14,
    };

    const registeredDataset = {
      label: lang.totalPreCheckinGuests,
      data: slots.map(({ registeredGuests }) => registeredGuests || 0),
      backgroundColor: REGISTERED_GUESTS_COLOR,
      ...barOptions,
    };

    if (guestView === GuestView.Registered) {
      return [registeredDataset];
    }

    if (guestView === GuestView.Total) {
      return [
        {
          label: lang.totalReservedGuests,
          data: slots.map(({ totalGuests }) => totalGuests || 0),
          backgroundColor: TOTAL_GUESTS_COLOR,
          ...barOptions,
        },
      ];
    }

    return [
      registeredDataset,
      {
        // remainder of the column, so the full column height is the total guests
        label: lang.totalReservedGuests,
        data: slots.map(({ registeredGuests, totalGuests }) =>
          Math.max((totalGuests || 0) - (registeredGuests || 0), 0)
        ),
        backgroundColor: TOTAL_GUESTS_COLOR,
        ...barOptions,
      },
    ];
  }, [slots, guestView]);

  return (
    <div style={{ height, position: "relative", width: "100%" }}>
      <Bar
        plugins={[stackTotalPlugin]}
        data={{ labels, datasets }}
        options={{
          maintainAspectRatio: false,
          responsive: true,
          layout: { padding: { top: 24 } },
          plugins: {
            legend: {
              position: "top",
              align: "end",
              labels: { usePointStyle: true, boxWidth: 8 },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: ({ datasetIndex, dataIndex }) => {
                  const slot = slots[dataIndex] || {};
                  const showsRegistered =
                    guestView === GuestView.Registered ||
                    (guestView === GuestView.Both && datasetIndex === 0);
                  if (showsRegistered) {
                    return `${lang.totalPreCheckinGuests}: ${slot.registeredGuests || 0}`;
                  }
                  return `${lang.totalReservedGuests}: ${slot.totalGuests || 0}`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { autoSkip: false, maxRotation: 90, minRotation: 45 },
              title: { display: true, text: lang.arrivalTime },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grace: "10%",
              ticks: { precision: 0 },
              grid: { borderDash: [4, 4] },
              title: { display: true, text: lang.guestCount },
            },
          },
          interaction: {
            mode: "index",
            intersect: false,
          },
        }}
      />
    </div>
  );
};

export default ArrivalTimeChart;
